import axios from "axios";

// External KohimaTaxi backend (shared with the mobile app). All routes are /api/*.
// Prefer REACT_APP_BACKEND_URL (set correctly in BOTH preview and production by the
// platform); REACT_APP_API_URL is only a legacy fallback.
export const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "";

export const TOKEN_KEY = "kt_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export const http = axios.create({ baseURL: `${API_BASE}/api`, timeout: 30000 });

http.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Retry once on transient failures (network error / 502-503-504 gateway blips),
// never on 4xx (e.g. 429 rate limit) so we don't make rate limiting worse.
const MAX_RETRIES = 1;
const isTransient = (err) => {
  const status = err?.response?.status;
  if (status) return [502, 504].includes(status);
  return err?.code === "ERR_NETWORK" || err?.message === "Network Error";
};
http.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err?.config;
    if (config && isTransient(err)) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        await new Promise((r) => setTimeout(r, 1200));
        return http(config);
      }
    }
    return Promise.reject(err);
  },
);

export function errorMessage(err, fallback = "Something went wrong") {
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error")
    return "Can't reach the server right now. Please check your connection and try again in a moment.";
  if (err?.code === "ECONNABORTED")
    return "The request timed out. Please try again in a moment.";
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  // Prefer the backend's own message (e.g. a friendly 503 "not available") over a generic one.
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc) ? d.loc.slice(1).join(".") : "";
        return field ? `${field}: ${d.msg}` : d.msg;
      })
      .join("; ");
  }
  if (status && status >= 500)
    return "The server is temporarily unavailable. Please try again in a moment.";
  if (!detail) return err?.message || fallback;
  return fallback;
}

// ---------- Auth ----------
export const authApi = {
  signup: (body) => http.post("/auth/signup", body).then((r) => r.data),
  signupStart: (body) => http.post("/auth/signup/start", body).then((r) => r.data),
  signupVerify: (body) => http.post("/auth/signup/verify", body).then((r) => r.data),
  signupVerifyPhone: (body) => http.post("/auth/signup/verify-phone", body).then((r) => r.data),
  signupResendPhone: (body) => http.post("/auth/signup/resend-phone", body).then((r) => r.data),
  signupResend: (body) => http.post("/auth/signup/resend", body).then((r) => r.data),
  login: (body) => http.post("/auth/login", body).then((r) => r.data),
  snoozeEmailReminder: () => http.post("/auth/email-reminder/snooze").then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  acceptTerms: () => http.post("/legal/accept").then((r) => r.data),
  switchRole: (role) => http.post("/auth/switch-role", { role }).then((r) => r.data),
  deleteAccount: () => http.delete("/auth/me").then((r) => r.data),
  updateProfile: (body) => http.patch("/auth/me", body).then((r) => r.data),
  profileProgress: () => http.get("/me/profile-progress").then((r) => r.data),
  changePassword: (body) => http.post("/auth/change-password", body).then((r) => r.data),
  emailChangeRequest: (new_email) => http.post("/auth/email-change/request", { new_email }).then((r) => r.data),
  emailChangeVerify: (otp) => http.post("/auth/email-change/verify", { otp }).then((r) => r.data),
  otpRequest: (body) => http.post("/auth/otp/request", body).then((r) => r.data),
  otpVerify: (body) => http.post("/auth/otp/verify", body).then((r) => r.data),
  passwordResetRequest: (identifier) => http.post("/auth/password-reset/request", { identifier }).then((r) => r.data),
  passwordResetVerify: (body) => http.post("/auth/password-reset/verify", body).then((r) => r.data),
  passwordResetComplete: (body) => http.post("/auth/password-reset/complete", body).then((r) => r.data),
  otpCompleteProfile: (body) => http.post("/auth/otp/complete-profile", body).then((r) => r.data),
  linkPhone: (body) => http.post("/auth/link-phone", body).then((r) => r.data),
  google: (body) => http.post("/auth/google", body).then((r) => r.data),
};

// ---------- Public ----------
export const publicApi = {
  stories: () => http.get("/stories").then((r) => r.data.stories || []),
  story: (slug) => http.get(`/stories/${slug}`).then((r) => r.data.story || r.data),
  notificationsStatus: () => http.get("/notifications/status").then((r) => r.data),
  trip: (token) => axios.get(`${API_BASE}/api/public/trip/${token}`).then((r) => r.data),
  rateTrip: (token, body) => axios.post(`${API_BASE}/api/public/trip/${token}/rate`, body).then((r) => r.data),
  tipCreate: (token, amount) => axios.post(`${API_BASE}/api/public/trip/${token}/tip/create`, { amount }).then((r) => r.data),
  tipVerify: (token, body) => axios.post(`${API_BASE}/api/public/trip/${token}/tip/verify`, body).then((r) => r.data),
  partnerTrust: (id) => axios.get(`${API_BASE}/api/partners/${id}/trust`).then((r) => r.data),
};

// ---------- Rides ----------
export const ridesApi = {
  estimate: (body) => http.post("/rides/estimate", body).then((r) => r.data),
  nearby: (lat, lng) => http.get("/drivers/nearby", { params: { lat, lng } }).then((r) => r.data.drivers || []),
  book: (body) => http.post("/rides", body).then((r) => r.data),
  list: () => http.get("/rides").then((r) => (Array.isArray(r.data) ? r.data : r.data.rides || [])),
  get: (id) => http.get(`/rides/${id}`).then((r) => r.data),
  invoicePdfUrl: (id) => `${API_BASE}/api/receipts/ride/${id}/pdf?auth=${encodeURIComponent(getToken() || "")}`,
  status: (id, status, extra = {}) => http.post(`/rides/${id}/status`, { status, ...extra }).then((r) => r.data),
  cancel: (id, body) => http.post(`/rides/${id}/cancel`, body || {}).then((r) => r.data),
  goodsEdit: (id, body) => http.patch(`/rides/${id}/goods-edit`, body || {}).then((r) => r.data),
  broadcastAll: (id) => http.post(`/rides/${id}/broadcast-all`).then((r) => r.data),
  end: (id, body) => http.post(`/rides/${id}/end`, body || {}).then((r) => r.data),
  intercityCharges: (id, body) => http.post(`/rides/${id}/intercity-charges`, body || {}).then((r) => r.data),
  choosePayment: (id, method) => http.post(`/rides/${id}/choose-payment`, { method }).then((r) => r.data),
  cashCollected: (id) => http.post(`/rides/${id}/cash-collected`).then((r) => r.data),
  addStop: (id, body) => http.post(`/rides/${id}/add-stop`, body).then((r) => r.data),
  changeDrop: (id, body) => http.post(`/rides/${id}/change-drop`, body).then((r) => r.data),
  removeStop: (id, index) => http.post(`/rides/${id}/remove-stop`, { index }).then((r) => r.data),
  widen: (id) => http.post(`/rides/${id}/widen`).then((r) => r.data),
  retrySearch: (id) => http.post(`/rides/${id}/retry-search`).then((r) => r.data),
  driverLocation: (id) => http.get(`/rides/${id}/driver-location`).then((r) => r.data),
  etaPing: (id, minutes) => http.post(`/rides/${id}/eta-ping`, { minutes }).then((r) => r.data),
  arrived: (id) => http.post(`/rides/${id}/arrived`).then((r) => r.data),
  noShowCancel: (id) => http.post(`/rides/${id}/driver-cancel`).then((r) => r.data),
  driverRelease: (id, body) => http.post(`/rides/${id}/driver-release`, body || {}).then((r) => r.data),
  refundDeposit: (id) => http.post(`/rides/${id}/refund-deposit`).then((r) => r.data),
  gstInvoice: (id) => http.get(`/rides/${id}/gst-invoice`).then((r) => r.data),
  cancellationFees: () => http.get(`/me/cancellation-fees`).then((r) => r.data),
  disputeFee: (feeId, reason, photos) => http.post(`/me/cancellation-fees/${feeId}/dispute`, { reason, photos }).then((r) => r.data),
  appealFee: (feeId, reason) => http.post(`/me/cancellation-fees/${feeId}/appeal`, { reason }).then((r) => r.data),
  riderReliability: () => http.get(`/me/reliability`).then((r) => r.data),
  share: (id) => http.post(`/rides/${id}/share`).then((r) => r.data),
  riderLocation: (id, lat, lng) => http.post(`/rides/${id}/rider-location`, { lat, lng }).then((r) => r.data),
  unshare: (id) => http.delete(`/rides/${id}/share`).then((r) => r.data),
  notifyReceiver: (id) => http.post(`/rides/${id}/notify-receiver`).then((r) => r.data),
  accept: (id, loc) => http.post(`/rides/${id}/accept`, null, { params: loc }).then((r) => r.data),
  decline: (id) => http.post(`/rides/${id}/decline`).then((r) => r.data),
  feeResponse: (id, approve) => http.post(`/rides/${id}/fee-response`, { approve }).then((r) => r.data),
  start: (id, code) => http.post(`/rides/${id}/start`, { code }).then((r) => r.data),
  pay: (id, payment_method) => http.post(`/rides/${id}/pay`, { payment_method }).then((r) => r.data),
  rate: (id, body) => http.post(`/rides/${id}/rate`, body).then((r) => r.data),
  schedule: (body) => http.post("/rides/schedule", body).then((r) => r.data),
  pickupPhoto: (id, body) => http.post(`/rides/${id}/pickup-photo`, body).then((r) => r.data),
  deliveryProof: (id, body) => http.post(`/rides/${id}/delivery-proof`, body).then((r) => r.data),
  photoUrl: (rideId, photoId) => `${API_BASE}/api/rides/${rideId}/photo/${photoId}?auth=${encodeURIComponent(getToken() || "")}`,
  receivers: () => http.get("/me/receivers").then((r) => r.data.receivers || []),
  recentPickups: () => http.get("/me/recent-pickups").then((r) => (Array.isArray(r.data) ? r.data : [])),
  addReceiver: (body) => http.post("/me/receivers", body).then((r) => r.data),
  deleteReceiver: (id) => http.delete(`/me/receivers/${id}`).then((r) => r.data),
  scheduled: () => http.get("/me/scheduled-rides").then((r) => r.data.schedules || []),
  dispatchScheduleNow: (id) => http.post(`/me/scheduled-rides/${id}/dispatch-now`).then((r) => r.data),
  deleteScheduled: (id) => http.delete(`/me/scheduled-rides/${id}`).then((r) => r.data),
};

// ---------- Private Cars (luxury rental) ----------
export const customerApi = {
  nearbyDrivers: (lat, lng, radiusKm = 5) => http.get("/customer/nearby-drivers", { params: { lat, lng, radius_km: radiusKm } }).then((r) => r.data.drivers || []),
  refunds: () => http.get("/me/refunds").then((r) => r.data),
};

export const configApi = {
  fares: () => http.get("/config/fares").then((r) => r.data),
  driverHeatmap: (lat, lng, radiusKm = 8) => http.get("/map/driver-heatmap", { params: { lat, lng, radius_km: radiusKm } }).then((r) => r.data),
  demandHeatmap: (lat, lng, radiusKm = 8) => http.get("/map/demand-heatmap", { params: { lat, lng, radius_km: radiusKm } }).then((r) => r.data),
  demandForecast: (lat, lng, radiusKm = 8) => http.get("/map/demand-forecast", { params: { lat, lng, radius_km: radiusKm } }).then((r) => r.data),
  demandTimeline: (lat, lng, radiusKm = 8) => http.get("/map/demand-timeline", { params: { lat, lng, radius_km: radiusKm } }).then((r) => r.data),
  pickupSuggestion: (lat, lng) => http.get("/map/pickup-suggestion", { params: { lat, lng } }).then((r) => r.data),
};


export const privateCarApi = {
  tripEstimate: (body) => http.post("/private-car/trip-estimate", body).then((r) => r.data),
  cars: (params) => http.get("/private-car/cars", { params }).then((r) => r.data.cars || []),
  book: (body) => http.post("/private-car/bookings", body).then((r) => r.data),
  payPlatformFee: (id, body) => http.post(`/private-car/bookings/${id}/pay-platform-fee`, body).then((r) => r.data.booking),
  mine: () => http.get("/private-car/bookings").then((r) => r.data.bookings || []),
  cancel: (id) => http.delete(`/private-car/bookings/${id}`).then((r) => r.data),
  pool: () => http.get("/private-car/pool").then((r) => r.data.bookings || []),
  driverBookings: () => http.get("/private-car/driver-bookings").then((r) => r.data.bookings || []),
  accept: (id) => http.post(`/private-car/bookings/${id}/accept`).then((r) => r.data.booking),
  reject: (id) => http.post(`/private-car/bookings/${id}/reject`).then((r) => r.data),
  complete: (id) => http.post(`/private-car/bookings/${id}/complete`).then((r) => r.data.booking),
  driverCancel: (id) => http.post(`/private-car/bookings/${id}/driver-cancel`).then((r) => r.data.booking),
  rate: (id, body) => http.post(`/private-car/bookings/${id}/rate`, body).then((r) => r.data),
  reviews: (driverId) => http.get(`/private-car/cars/${driverId}/reviews`).then((r) => r.data),
  myReviews: () => http.get("/private-car/my-reviews").then((r) => r.data),
  driverStats: () => http.get("/private-car/driver-stats").then((r) => r.data),
  replyReview: (reviewId, reply) => http.post(`/private-car/reviews/${reviewId}/reply`, { reply }).then((r) => r.data),
  reportReview: (reviewId, reason) => http.post(`/private-car/reviews/${reviewId}/report`, { reason: reason || null }).then((r) => r.data),
  reminderPrefs: (hours) => http.patch("/private-car/reminder-prefs", { hours }).then((r) => r.data),
  photos: () => http.get("/private-car/photos").then((r) => r.data.photos || []),
  uploadPhoto: (body) => http.post("/private-car/photos", body).then((r) => r.data),
  deletePhoto: (id) => http.delete(`/private-car/photos/${id}`).then((r) => r.data),
  photoUrl: (photoId) => `${API_BASE}/api/private-car/photo/${photoId}?auth=${encodeURIComponent(getToken() || "")}`,
  earningsExportUrl: (month) => `${API_BASE}/api/private-car/earnings-export?${month ? `month=${encodeURIComponent(month)}&` : ""}auth=${encodeURIComponent(getToken() || "")}`,
  invoicePdfUrl: (id) => `${API_BASE}/api/receipts/private-car/${id}/pdf?auth=${encodeURIComponent(getToken() || "")}`,
};

// ---------- In-app notifications ----------
export const notificationsApi = {
  list: () => http.get("/me/notifications").then((r) => r.data),
  markRead: (id) => http.post(`/me/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => http.post("/me/notifications/read-all").then((r) => r.data),
  pendingTasks: () => http.get("/me/pending-tasks").then((r) => r.data.tasks || []),
};

// ---------- Misconduct / harassment reports ----------
export const reportsApi = {
  file: (body) => http.post("/me/reports", body).then((r) => r.data),
  adminList: (status = "open") => http.get("/admin/reports", { params: { status } }).then((r) => r.data),
  adminDetail: (id) => http.get(`/admin/reports/${id}`).then((r) => r.data),
  mine: () => http.get("/me/reports").then((r) => r.data),
  photoUrl: (photoId) => `${API_BASE}/api/admin/reports/photo/${photoId}?auth=${encodeURIComponent(getToken() || "")}`,
  adminResolve: (id, action, note) => http.post(`/admin/reports/${id}/resolve`, { action, note }).then((r) => r.data),
  adminUnsuspend: (userId) => http.post(`/admin/users/${userId}/unsuspend`).then((r) => r.data),
};

// ---------- Blocking (user-to-user) ----------
export const blockApi = {
  list: () => http.get("/me/blocks").then((r) => r.data.blocks || []),
  block: (body) => http.post("/me/block", body).then((r) => r.data),
  unblock: (userId) => http.delete(`/me/block/${userId}`).then((r) => r.data),
};

// ---------- Driver ----------
export const driverApi = {
  readiness: () => http.get("/me/driver-readiness").then((r) => r.data),
  setTaxiScope: (scope) => http.post("/driver/taxi-scope", { scope }).then((r) => r.data),
  incoming: (lat, lng) => http.get("/driver/incoming", { params: { lat, lng } }).then((r) => r.data),
  rideRequests: (lat, lng) => http.get("/driver/ride-requests", { params: { lat, lng } }).then((r) => r.data.requests || []),
  deliveries: () => http.get("/driver/deliveries").then((r) => r.data),
  activeRide: () => http.get("/driver/active-ride").then((r) => r.data),
  nextJob: () => http.get("/driver/next-job").then((r) => r.data),
  feeSchedule: () => http.get("/driver/fee-schedule").then((r) => r.data),
  rideDetail: (rideId) => http.get(`/driver/history/ride/${rideId}`).then((r) => r.data),
  orderDetail: (orderId) => http.get(`/driver/history/order/${orderId}`).then((r) => r.data),
  claimDelivery: (orderId) => http.post(`/driver/deliveries/${orderId}/claim`).then((r) => r.data.order || r.data),
  online: (online) => http.post("/driver/online", { online }).then((r) => r.data),
  earnings: () => http.get("/driver/earnings").then((r) => r.data),
  earningsSummary: () => http.get("/driver/earnings/summary").then((r) => r.data),
  goodsRatings: () => http.get("/driver/goods-ratings").then((r) => r.data),
  postLocation: (lat, lng, heading) => http.post("/driver/location", { lat, lng, heading }).then((r) => r.data),
  earningsReport: (params) => http.get("/driver/earnings/report", { params }).then((r) => r.data),
  payoutStatements: () => http.get("/driver/payout-statements").then((r) => r.data.statements || []),
  downloadStatements: async (weeks = 8) => {
    const res = await http.get("/driver/payout-statements/export.pdf", { params: { weeks }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = "payout_statements.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  logEarning: (body) => http.post("/driver/earnings/log", body).then((r) => r.data),
  profile: (body) => http.post("/driver/profile", body).then((r) => r.data),
  permit: (body) => http.post("/driver/permit", body).then((r) => r.data),
};

// ---------- KYC ----------
export const kycApi = {
  mine: () => http.get("/me/verification").then((r) => r.data),
  upload: (body) => http.post("/me/verification/upload", body).then((r) => r.data),
};

// ---------- Food ----------
export const foodApi = {
  restaurants: (params) => http.get("/restaurants", { params }).then((r) => r.data.restaurants || []),
  cuisines: () => http.get("/restaurants/cuisines").then((r) => r.data.cuisines || []),
  restaurant: (id, params) => http.get(`/restaurants/${id}`, { params }).then((r) => r.data),
  createOrder: (body) => http.post("/orders", body).then((r) => r.data.order || r.data),
  deliveryQuote: (params) => http.get("/orders/delivery-quote", { params }).then((r) => r.data),
  orders: () => http.get("/orders").then((r) => r.data.orders || []),
  order: (id) => http.get(`/orders/${id}`).then((r) => r.data.order || r.data),
  invoicePdfUrl: (id) => `${API_BASE}/api/receipts/order/${id}/pdf?auth=${encodeURIComponent(getToken() || "")}`,
  orderStatus: (id, status, reason, coords, extra) => http.post(`/orders/${id}/status`, { status, reason, ...(coords || {}), ...(extra || {}) }).then((r) => r.data.order || r.data),
  etaPing: (id, minutes) => http.post(`/orders/${id}/eta-ping`, { minutes }).then((r) => r.data),
  readyTime: (id, minutes) => http.post(`/orders/${id}/ready-time`, { minutes }).then((r) => r.data),
  selfPickup: (id) => http.post(`/orders/${id}/self-pickup`).then((r) => r.data.order || r.data),
  noPartnerRefund: (id) => http.post(`/orders/${id}/no-partner-refund`).then((r) => r.data.order || r.data),
  driverArrived: (id) => http.post(`/orders/${id}/driver-arrived`).then((r) => r.data.order || r.data),
  reachedRestaurant: (id) => http.post(`/orders/${id}/reached-restaurant`).then((r) => r.data.order || r.data),
  reorder: (id) => http.get(`/orders/${id}/reorder`).then((r) => r.data),
  rateOrder: (id, body) => http.post(`/orders/${id}/rate`, body).then((r) => r.data),
  payOrder: (id, payment_method) => http.post(`/orders/${id}/pay`, { payment_method }).then((r) => r.data.order || r.data),
  createPayment: (id) => http.post(`/orders/${id}/payment/razorpay/create`).then((r) => r.data),
  verifyPayment: (id, body) => http.post(`/orders/${id}/payment/razorpay/verify`, body).then((r) => r.data.order || r.data),
  driverLocation: (id) => http.get(`/orders/${id}/driver-location`).then((r) => r.data),
  customerArrived: (id) => http.post(`/orders/${id}/arrived`).then((r) => r.data.order || r.data),
  customerEta: (id, etaMin) => http.post(`/orders/${id}/eta`, { eta_min: etaMin }).then((r) => r.data.order || r.data),
  promos: () => http.get("/promos").then((r) => r.data.promos || []),
  validatePromo: (body) => http.post("/promos/validate", body).then((r) => r.data),
  launchOffer: () => http.get("/food/launch-offer").then((r) => r.data),
};

// ---------- Restaurant owner ----------
export const restaurantApi = {
  mine: () => http.get("/me/restaurant").then((r) => r.data.restaurant),
  save: (body) => http.post("/me/restaurant", body).then((r) => r.data.restaurant || r.data),
  menu: () => http.get("/me/restaurant/menu").then((r) => r.data.menu || r.data.items || []),
  addItem: (body) => http.post("/me/restaurant/menu", body).then((r) => r.data.item || r.data),
  bulkAddItems: (items) => http.post("/me/restaurant/menu/bulk", { items }).then((r) => r.data),
  patchItem: (id, body) => http.patch(`/me/restaurant/menu/${id}`, body).then((r) => r.data.item || r.data),
  deleteItem: (id) => http.delete(`/me/restaurant/menu/${id}`).then((r) => r.data),
  orders: () => http.get("/me/restaurant/orders").then((r) => r.data.orders || []),
  driverEtaHistory: () => http.get("/me/restaurant/driver-eta-history").then((r) => r.data),
  flagDriver: (driver_id, flagged) => http.post("/me/restaurant/flag-driver", { driver_id, flagged }).then((r) => r.data),
  analytics: () => http.get("/me/restaurant/analytics").then((r) => r.data),
  setStatus: (isOpen, opts) => http.post("/me/restaurant/status", { is_open: isOpen, ...(opts || {}) }).then((r) => r.data.restaurant),
  setAutoSchedule: (auto) => http.post("/me/restaurant/status", { auto_schedule: auto }).then((r) => r.data.restaurant),
  setAutoAccept: (auto) => http.post("/me/restaurant/status", { auto_accept: auto }).then((r) => r.data.restaurant),
  setPickup: (enabled, prepMin) => http.post("/me/restaurant/status", { ...(enabled != null ? { pickup_enabled: enabled } : {}), ...(prepMin != null ? { pickup_prep_min: prepMin } : {}) }).then((r) => r.data.restaurant),
  offlineHistory: () => http.get("/me/restaurant/offline-history").then((r) => r.data.events || []),
  pause: (body) => http.post("/me/restaurant/pause", body).then((r) => r.data.restaurant),
  resume: () => http.post("/me/restaurant/resume").then((r) => r.data.restaurant),
  addCategory: (name) => http.post("/me/restaurant/categories", { name }).then((r) => r.data.categories || []),
  renameCategory: (oldName, newName) => http.patch("/me/restaurant/categories", { old: oldName, new: newName }).then((r) => r.data.categories || []),
  deleteCategory: (name) => http.delete(`/me/restaurant/categories/${encodeURIComponent(name)}`).then((r) => r.data.categories || []),
  discounts: () => http.get("/me/restaurant/discounts").then((r) => r.data.discounts || []),
  createDiscount: (body) => http.post("/me/restaurant/discounts", body).then((r) => r.data.discount),
  updateDiscount: (id, body) => http.patch(`/me/restaurant/discounts/${id}`, body).then((r) => r.data.discount),
  deleteDiscount: (id) => http.delete(`/me/restaurant/discounts/${id}`).then((r) => r.data),
  ratings: () => http.get("/me/restaurant/ratings").then((r) => r.data),
  complaints: () => http.get("/me/restaurant/complaints").then((r) => r.data),
  resolveComplaint: (orderId) => http.post(`/me/restaurant/complaints/${orderId}/resolve`).then((r) => r.data),
  sales: (params = {}) => http.get("/me/restaurant/sales", { params }).then((r) => r.data),
  orderDetail: (orderId) => http.get(`/me/restaurant/orders/${orderId}`).then((r) => r.data),
  partnerAvailability: (orderId) => http.get(`/me/restaurant/orders/${orderId}/partner-availability`).then((r) => r.data),
  rejectAnalytics: (params = {}) => http.get("/me/restaurant/reject-analytics", { params }).then((r) => r.data),
  cancelInsights: () => http.get("/me/restaurant/cancel-insights").then((r) => r.data),
  downloadSales: async (params, format) => {
    const res = await http.get(`/me/restaurant/sales/export.${format}`, { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `sales_report.${format}`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  managers: () => http.get("/me/restaurant/managers").then((r) => r.data.managers || []),
  addManager: (body) => http.post("/me/restaurant/managers", body).then((r) => r.data.manager),
  updateManager: (id, body) => http.patch(`/me/restaurant/managers/${id}`, body).then((r) => r.data.manager),
  removeManager: (id) => http.delete(`/me/restaurant/managers/${id}`).then((r) => r.data),
  galleryAdd: (image) => http.post("/me/restaurant/gallery", { image }).then((r) => r.data.gallery || []),
  galleryRemove: (image) => http.delete("/me/restaurant/gallery", { data: { image } }).then((r) => r.data.gallery || []),
  gallerySet: (images) => http.put("/me/restaurant/gallery", { images }).then((r) => r.data.gallery || []),
  addressChange: () => http.get("/me/restaurant/address-change").then((r) => r.data.request),
  requestAddressChange: (body) => http.post("/me/restaurant/address-change", body).then((r) => r.data.request),
  cancelAddressChange: () => http.post("/me/restaurant/address-change/cancel").then((r) => r.data),
};

// ---------- Addresses ----------
export const addressApi = {
  list: () => http.get("/me/addresses").then((r) => r.data.addresses || []),
  add: (body) => http.post("/me/addresses", body).then((r) => r.data),
  patch: (id, body) => http.patch(`/me/addresses/${id}`, body).then((r) => r.data),
  remove: (id) => http.delete(`/me/addresses/${id}`).then((r) => r.data),
  recentDrops: () => http.get("/me/recent-drops").then((r) => r.data.drops || []),
  recentPlaces: (limit = 8) => http.get("/me/recent-places", { params: { limit } }).then((r) => r.data.places || []),
  staticMapUrl: (lat, lng) => `${API_BASE}/api/media/staticmap?lat=${lat}&lng=${lng}&auth=${encodeURIComponent(getToken() || "")}`,
};

// ---------- Maps (Google Places via backend proxy) ----------
export const mapsApi = {
  config: () => http.get("/maps/config").then((r) => r.data),
  autocomplete: (input, session) => http.get("/maps/autocomplete", { params: { input, session } }).then((r) => r.data),
  place: (place_id) => http.get("/maps/place", { params: { place_id } }).then((r) => r.data),
  reverse: (lat, lng) => http.get("/maps/reverse", { params: { lat, lng } }).then((r) => r.data),
  popularNearby: (lat, lng) => http.get("/maps/popular-nearby", { params: { lat, lng } }).then((r) => r.data.places || []),
  directions: (origin, destination) => http.get("/maps/directions", { params: { origin, destination } }).then((r) => r.data),
  navigate: (origin, destination, waypoints) => http.get("/maps/navigate", { params: { origin, destination, ...(waypoints ? { waypoints } : {}) } }).then((r) => r.data),
};

// ---------- Payouts ----------
export const payoutApi = {
  getAccount: () => http.get("/payout/account").then((r) => r.data.account),
  saveAccount: (body) => http.post("/payout/account", body).then((r) => r.data.account),
  me: () => http.get("/payout/me").then((r) => r.data),
  // admin
  adminList: (status) => http.get("/admin/payouts", { params: status ? { status } : {} }).then((r) => r.data),
  adminRuns: () => http.get("/admin/payout-runs").then((r) => r.data.runs || []),
  adminRun: () => http.post("/admin/payouts/run").then((r) => r.data),
  adminMarkProcessed: (id, body = {}) => http.post(`/admin/payouts/${id}/mark-processed`, body).then((r) => r.data.payout),
  adminMarkCollected: (id, body = {}) => http.post(`/admin/payouts/${id}/mark-collected`, body).then((r) => r.data.payout),
  adminDisburse: (id) => http.post(`/admin/payouts/${id}/disburse`).then((r) => r.data),
  adminSettings: () => http.get("/admin/payouts/settings").then((r) => r.data),
  adminSaveSettings: (body) => http.put("/admin/payouts/settings", body).then((r) => r.data),
  receiptUrl: (id) => `${API_BASE}/api/payout/${id}/receipt.pdf?auth=${encodeURIComponent(getToken() || "")}`,
  gstInvoiceUrl: (id) => `${API_BASE}/api/payout/${id}/gst-invoice.pdf?auth=${encodeURIComponent(getToken() || "")}`,
  adminGstInvoices: (q) => http.get("/admin/gst-invoices", { params: q ? { q } : {} }).then((r) => r.data),
  adminResendInvoices: () => http.post("/admin/payouts/resend-invoices").then((r) => r.data),
  commissionInvoiceUrl: (month) => `${API_BASE}/api/me/restaurant/commission-invoice.pdf?${month ? `month=${encodeURIComponent(month)}&` : ""}auth=${encodeURIComponent(getToken() || "")}`,
  gstRemittance: (params) => http.get("/admin/gst/remittance", { params }).then((r) => r.data),
  gstRemittanceCsvUrl: (params) => `${API_BASE}/api/admin/gst/remittance/export.csv?${new URLSearchParams(params || {}).toString()}&auth=${encodeURIComponent(getToken() || "")}`,
  gstRemittancePdfUrl: (params) => `${API_BASE}/api/admin/gst/remittance/export.pdf?${new URLSearchParams(params || {}).toString()}&auth=${encodeURIComponent(getToken() || "")}`,
  commissionGstCsvUrl: (month) => `${API_BASE}/api/admin/gst/commission-export.csv?${month ? `month=${encodeURIComponent(month)}&` : ""}auth=${encodeURIComponent(getToken() || "")}`,
};

// ---------- Food COD daily deposit ----------
export const codApi = {
  // driver
  driverSummary: () => http.get("/driver/cod/summary").then((r) => r.data),
  payManual: (reference, method = "upi") => http.post("/driver/cod/pay-manual", { reference, method }).then((r) => r.data.deposit),
  rzpCreate: () => http.post("/driver/cod/razorpay/create").then((r) => r.data),
  rzpCheck: (deposit_id) => http.post("/driver/cod/razorpay/check", { deposit_id }).then((r) => r.data),
  // admin / staff
  adminSettings: () => http.get("/admin/settings/cod-payment").then((r) => r.data.settings),
  adminSaveSettings: (body) => http.put("/admin/settings/cod-payment", body).then((r) => r.data.settings),
  adminDeposits: () => http.get("/admin/cod/deposits").then((r) => r.data),
  adminOutstanding: () => http.get("/admin/cod/outstanding").then((r) => r.data),
  adminOutstandingDriver: (driverId) => http.get(`/admin/cod/outstanding/${driverId}`).then((r) => r.data),
  adminVerify: (id) => http.post(`/admin/cod/deposits/${id}/verify`).then((r) => r.data.deposit),
  adminCollect: (driverId, body) => http.post(`/admin/cod/collect/${driverId}`, body || {}).then((r) => r.data.deposit),
  receiptUrl: (id) => `${API_BASE}/api/receipts/cod/${id}/pdf?auth=${encodeURIComponent(getToken() || "")}`,
};

// ---------- Chat ----------
export const chatApi = {
  summary: () => http.get("/chats/summary").then((r) => r.data),
  inbox: () => http.get("/chats/inbox").then((r) => r.data),
  messages: (chatId, since) => http.get(`/chats/${chatId}/messages`, { params: since ? { since } : {} }).then((r) => r.data),
  send: (chatId, text, image) => http.post(`/chats/${chatId}/messages`, { text, image }).then((r) => r.data.message || r.data),
  imageUrl: (chatId, imageId) => `${API_BASE}/api/chats/${chatId}/image/${imageId}?auth=${encodeURIComponent(getToken() || "")}`,
};

export const favApi = {
  list: () => http.get("/me/favourites").then((r) => r.data),
  add: (restaurantId) => http.post(`/me/favourites/${restaurantId}`).then((r) => r.data),
  remove: (restaurantId) => http.delete(`/me/favourites/${restaurantId}`).then((r) => r.data),
};

// ---------- Images ----------
export const imageApi = {
  upload: (file_data, mime) => http.post("/images/upload", { file_data, mime }).then((r) => r.data),
  url: (id) => `${API_BASE}/api/images/${id}`,
};

// ---------- Admin ----------
export const adminApi = {
  verifications: (params) => http.get("/admin/verifications", { params }).then((r) => r.data.users || []),
  cancellationDisputes: (status = "open") => http.get("/admin/cancellation-disputes", { params: { status } }).then((r) => r.data),
  resolveCancellationFee: (feeId, action, note) => http.post(`/admin/cancellation-fees/${feeId}/resolve`, { action, note }).then((r) => r.data),
  feeDisputePhotoUrl: (photoId) => `${API_BASE}/api/admin/cancellation-fees/photo/${photoId}?auth=${encodeURIComponent(getToken() || "")}`,
  users: (params) => http.get("/admin/users", { params }).then((r) => r.data),
  userDetail: (userId) => http.get(`/admin/users/${userId}`).then((r) => r.data),
  adminSuspend: (userId, reason) => http.post(`/admin/users/${userId}/suspend`, { reason }).then((r) => r.data),
  adminUnsuspend: (userId) => http.post(`/admin/users/${userId}/unsuspend`).then((r) => r.data),
  adminDeleteUser: (userId) => http.delete(`/admin/users/${userId}`).then((r) => r.data),
  updateUser: (userId, body) => http.patch(`/admin/users/${userId}`, body).then((r) => r.data.user),
  restaurants: (params) => http.get("/admin/restaurants", { params }).then((r) => r.data.restaurants || []),
  restaurant: (rid) => http.get(`/admin/restaurants/${rid}`).then((r) => r.data.restaurant),
  updateRestaurant: (rid, body) => http.patch(`/admin/restaurants/${rid}`, body).then((r) => r.data.restaurant),
  restaurantMenu: (rid) => http.get(`/admin/restaurants/${rid}/menu`).then((r) => r.data.items || []),
  addRestaurantItem: (rid, body) => http.post(`/admin/restaurants/${rid}/menu`, body).then((r) => r.data.item),
  patchRestaurantItem: (rid, itemId, body) => http.patch(`/admin/restaurants/${rid}/menu/${itemId}`, body).then((r) => r.data.item),
  deleteRestaurantItem: (rid, itemId) => http.delete(`/admin/restaurants/${rid}/menu/${itemId}`).then((r) => r.data),
  addRestaurantCategory: (rid, name) => http.post(`/admin/restaurants/${rid}/categories`, { name }).then((r) => r.data.categories || []),
  bulkAddRestaurantItems: (rid, items) => http.post(`/admin/restaurants/${rid}/menu/bulk`, { items }).then((r) => r.data),
  verificationCounts: () => http.get("/admin/verifications/counts").then((r) => r.data),
  verification: (userId) => http.get(`/admin/verifications/${userId}`).then((r) => r.data),
  docUrl: (userId, docType, download = false) =>
    `${API_BASE}/api/admin/verifications/${userId}/doc/${docType}?token=${encodeURIComponent(getToken() || "")}${download ? "&download=true" : ""}`,
  approve: (userId, rekycDate, opts = {}) => http.post(`/admin/verifications/${userId}/approve`, { rekyc_date: rekycDate || null, skip_docs: !!opts.skipDocs }).then((r) => r.data),
  setRekyc: (userId, rekycDate) => http.post(`/admin/verifications/${userId}/rekyc`, { rekyc_date: rekycDate }).then((r) => r.data),
  rekycExpiring: () => http.get("/admin/rekyc/expiring").then((r) => r.data),
  rekycRemind: (userId) => http.post(`/admin/rekyc/${userId}/remind`).then((r) => r.data),
  requestRekyc: (userId) => http.post(`/admin/verifications/${userId}/request-rekyc`).then((r) => r.data),
  noPartnerReport: (days = 30) => http.get("/admin/reports/no-partner", { params: { days } }).then((r) => r.data),
  reject: (userId, reason) => http.post(`/admin/verifications/${userId}/reject`, { reason }).then((r) => r.data),
  setTaxiScope: (userId, scope) => http.post(`/admin/users/${userId}/taxi-scope`, { scope }).then((r) => r.data),
  setDriverType: (userId, vehicleCategory, taxiScope) => http.post(`/admin/users/${userId}/driver-type`, { vehicle_category: vehicleCategory, ...(taxiScope ? { taxi_scope: taxiScope } : {}) }).then((r) => r.data),
  permits: (params) => http.get("/admin/permits", { params }).then((r) => r.data.users || []),
  permitCounts: () => http.get("/admin/permits/counts").then((r) => r.data),
  approvePermit: (userId) => http.post(`/admin/permits/${userId}/approve`).then((r) => r.data),
  rejectPermit: (userId, reason) => http.post(`/admin/permits/${userId}/reject`, { reason }).then((r) => r.data),
  stories: () => http.get("/admin/stories").then((r) => r.data.stories || []),
  story: (id) => http.get(`/admin/stories/${id}`).then((r) => r.data.story || r.data),
  createStory: (body) => http.post("/admin/stories", body).then((r) => r.data.story || r.data),
  patchStory: (id, body) => http.patch(`/admin/stories/${id}`, body).then((r) => r.data.story || r.data),
  deleteStory: (id) => http.delete(`/admin/stories/${id}`).then((r) => r.data),
  internalTasks: (status = "open") => http.get("/admin/internal-tasks", { params: { status } }).then((r) => r.data),
  internalTaskCounts: () => http.get("/admin/internal-tasks/counts").then((r) => r.data),
  resolveInternalTask: (id) => http.post(`/admin/internal-tasks/${id}/resolve`).then((r) => r.data.task),
  staff: () => http.get("/admin/staff").then((r) => r.data),
  createStaff: (body) => http.post("/admin/staff", body).then((r) => r.data.staff),
  updateStaff: (id, body) => http.patch(`/admin/staff/${id}`, body).then((r) => r.data.staff),
  suspendStaff: (id, suspended) => http.post(`/admin/staff/${id}/suspend`, { suspended }).then((r) => r.data.staff),
  deleteStaff: (id) => http.delete(`/admin/staff/${id}`).then((r) => r.data),
  staffSessions: (id) => http.get(`/admin/staff/${id}/sessions`).then((r) => r.data),
  bulkAssignManager: (agentIds, managerId) => http.post("/admin/staff/bulk-assign-manager", { agent_ids: agentIds, manager_id: managerId || "" }).then((r) => r.data),
  uploadDocOnBehalf: (userId, body) => http.post(`/admin/verifications/${userId}/upload-doc`, body).then((r) => r.data),
  setStaffOnline: (online) => http.post("/staff/online", { online }).then((r) => r.data),
  photoModeration: (status) => http.get("/admin/photo-moderation", { params: { status } }).then((r) => r.data.photos || []),
  photoModerationCounts: () => http.get("/admin/photo-moderation/counts").then((r) => r.data),
  approvePhoto: (id) => http.post(`/admin/photo-moderation/${id}/approve`).then((r) => r.data),
  rejectPhoto: (id) => http.post(`/admin/photo-moderation/${id}/reject`).then((r) => r.data),
  bulkModerate: (photoIds, action) => http.post("/admin/photo-moderation/bulk", { photo_ids: photoIds, action }).then((r) => r.data),
  flaggedReviews: () => http.get("/admin/flagged-reviews").then((r) => r.data),
  removeReview: (id) => http.post(`/admin/reviews/${id}/remove`).then((r) => r.data),
  dismissFlag: (id) => http.post(`/admin/reviews/${id}/dismiss-flag`).then((r) => r.data),
  versionConfig: () => http.get("/app/version-config").then((r) => r.data),
  setVersionConfig: (body) => http.put("/admin/app/version-config", body).then((r) => r.data),
  liveOrders: () => http.get("/admin/live-orders").then((r) => r.data),
  pastOrders: (type = "all", params = {}) => http.get("/admin/past-orders", { params: { type, ...params } }).then((r) => r.data),
  pastOrdersExportUrl: (type = "all", params = {}) => {
    const qs = new URLSearchParams({ type, auth: getToken() || "", ...params });
    return `${API_BASE}/api/admin/past-orders/export?${qs.toString()}`;
  },
  cancelLiveOrder: (kind, id, reason) => http.post(`/admin/live-orders/${kind}/${id}/cancel`, { reason }).then((r) => r.data),
  resolveRidePayment: (rideId, action, reason) => http.post(`/admin/live-orders/ride/${rideId}/resolve-payment`, { action, reason }).then((r) => r.data),
  completeLiveRide: (rideId, reason) => http.post(`/admin/live-orders/ride/${rideId}/complete`, { reason }).then((r) => r.data),
  orderDetail: (kind, id) => http.get(`/admin/order/${kind}/${id}`).then((r) => r.data.detail),
  financialReport: (params = {}) => http.get("/admin/financial-report", { params }).then((r) => r.data),
  ridePhotoUrl: (rideId, photoId) => `${API_BASE}/api/rides/${rideId}/photo/${photoId}?auth=${getToken() || ""}`,
  sosList: (status) => http.get("/admin/sos", { params: { status } }).then((r) => r.data),
  sosActiveCount: () => http.get("/admin/sos/active-count").then((r) => r.data),
  ackSos: (id) => http.post(`/admin/sos/${id}/ack`).then((r) => r.data),
  resolveSos: (id, note) => http.post(`/admin/sos/${id}/resolve`, { note }).then((r) => r.data),
  sosComment: (id, text) => http.post(`/admin/sos/${id}/comment`, { text }).then((r) => r.data),
  staffHeartbeat: () => http.post("/staff/heartbeat").then((r) => r.data),
  staffShifts: () => http.get("/admin/staff-shifts").then((r) => r.data),
  addStaffShift: (body) => http.post("/admin/staff-shifts", body).then((r) => r.data.shift),
  deleteStaffShift: (id) => http.delete(`/admin/staff-shifts/${id}`).then((r) => r.data),
  copyDayShifts: (body) => http.post("/admin/staff-shifts/copy-day", body).then((r) => r.data),
  staffOnlineReport: (days = 14) => http.get("/admin/staff/online-report", { params: { days } }).then((r) => r.data),
  sosResponseReport: (weeks = 8) => http.get("/admin/sos/response-report", { params: { weeks } }).then((r) => r.data),
  vehicleClassInsights: (days = 30) => http.get("/admin/insights/vehicle-classes", { params: { days } }).then((r) => r.data),
  staffList: () => http.get("/admin/staff").then((r) => r.data),
  addressChanges: (status = "pending") => http.get("/admin/restaurant-address-changes", { params: { status } }).then((r) => r.data),
  approveAddressChange: (id) => http.post(`/admin/restaurant-address-changes/${id}/approve`).then((r) => r.data),
  rejectAddressChange: (id, reason) => http.post(`/admin/restaurant-address-changes/${id}/reject`, { reason }).then((r) => r.data),
};

export const sosApi = {
  raise: (body) => http.post("/sos", body).then((r) => r.data),
};

// ---------- Helpers ----------
export const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

export const inr2 = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const formatDate = (iso, opts = {}) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", ...opts });
};

// DD/MM/YYYY HH:MM (24h) — used across admin order/payout/SOS views.
export const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const titleCase = (s = "") => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
export const roleLabel = (r = "") => (r === "rider" ? "Customer" : titleCase(r));
